import _slicedToArray from '@babel/runtime/helpers/slicedToArray';
import React, { useState, useEffect } from 'react';
import { string, func, node, shape, bool } from 'prop-types';
import { useHistory } from 'react-router-dom';
import { Heading, Icon } from '@tesla/design-system-react';
import { iconChevron180 } from '@tesla/design-system-icons';
import DrawerLoader from './DrawerLoader/index.js';
import '@tesla/design-system/dist/index.css';
import styles from './styles.module.css.js';
import { gaInteractionHandler } from '../../utils/googleAnalytics/index.js';

var propTypes = {
  /** Drawer header */
  title: string,
  /** Drawer subheader */
  subtitle: string,
  /** Called after drawer expanded state is toggled */
  handleDrawerChange: func,
  /** Any React child node, but should usually be a series of ToggleDrawerList */
  children: node,
  /** Used to identify drawer when router-initializing state */
  id: string,
  /** Used for google analytics */
  gaData: shape({
    event: string,
    interaction: string,
    label: string
  }),
  /** Determines if the loader should be shown or not */
  isLoading: bool,
  /** Data test to add specificity between drawers */
  dataTestSuffix: string
};

/* istanbul ignore next */
var defaultProps = {
  title: null,
  subtitle: null,
  handleDrawerChange: function handleDrawerChange() {},
  children: null,
  id: null,
  gaData: null,
  isLoading: false,
  dataTestSuffix: ''
};

/**
 * Collapsible drawer
 * @extends {Component<Props>}
 */
var ToggleDrawer = function ToggleDrawer(_ref) {
  var title = _ref.title,
    subtitle = _ref.subtitle,
    handleDrawerChange = _ref.handleDrawerChange,
    children = _ref.children,
    id = _ref.id,
    gaData = _ref.gaData,
    isLoading = _ref.isLoading,
    dataTestSuffix = _ref.dataTestSuffix;
  var history = useHistory();
  var _useState = useState(false),
    _useState2 = _slicedToArray(_useState, 2),
    isExpanded = _useState2[0],
    setIsExpanded = _useState2[1];
  var formattedDataSuffix = dataTestSuffix ? "-".concat(dataTestSuffix) : '';
  useEffect(function () {
    var hash = history.location.hash;
    if (id && id.split('_').includes(hash.substring(1))) {
      setIsExpanded(true);
    }
  }, []);
  var toggleDrawerContent = function toggleDrawerContent() {
    if (!isExpanded) {
      gaInteractionHandler(gaData);
    }
    setIsExpanded(!isExpanded);
    handleDrawerChange('isExpanded', isExpanded);
  };
  return /*#__PURE__*/React.createElement("div", {
    className: styles.ToggleDrawer,
    "data-test": isExpanded ? "toggle-drawer".concat(formattedDataSuffix, "-expanded") : "toggle-drawer".concat(formattedDataSuffix)
  }, /*#__PURE__*/React.createElement("button", {
    type: "button",
    className: "".concat(styles['ToggleDrawer--Header'], " focus-visible"),
    onClick: toggleDrawerContent,
    "aria-expanded": isExpanded
  }, /*#__PURE__*/React.createElement("div", {
    className: styles['ToggleDrawer--Header-Container']
  }, /*#__PURE__*/React.createElement("div", {
    className: styles['Header--Title']
  }, /*#__PURE__*/React.createElement(Heading, {
    is: "h2",
    looksLike: "h4",
    "data-test": "toggle-drawer-title".concat(formattedDataSuffix)
  }, title), /*#__PURE__*/React.createElement("div", {
    className: styles.DownArrow,
    "data-test": "toggle-drawer-icon".concat(formattedDataSuffix)
  }, /*#__PURE__*/React.createElement(Icon, {
    data: iconChevron180,
    className: styles.DownArrowIcon
  }))), /*#__PURE__*/React.createElement("span", {
    className: "tds--is_visually_hidden",
    "aria-hidden": "true"
  }, ":"), subtitle && /*#__PURE__*/React.createElement("p", {
    className: "tds-text-eyebrow tds-text-eyebrow--10 ".concat(styles['Header--Subtitle']),
    "data-test": "toggle-drawer-subtitle".concat(formattedDataSuffix)
  }, subtitle))), /*#__PURE__*/React.createElement("div", {
    className: styles['ToggleDrawer--Content'],
    "aria-hidden": !isExpanded,
    ref: function ref(e) {
      if (e) e.style.maxHeight = isExpanded ? "".concat(e.scrollHeight, "px") : '0px';
    }
  }, isLoading ? /*#__PURE__*/React.createElement(DrawerLoader, {
    loaderId: id
  }) : children));
};
ToggleDrawer.propTypes = propTypes;
ToggleDrawer.defaultProps = defaultProps;

export { ToggleDrawer as default };
